import type EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import type FindMeetingLocationsResponse from 'owa-calendar-scheduling-service/lib/schema/FindMeetingLocationsResponse';
import type LocationSourceType from 'owa-service/lib/contract/LocationSourceType';
import type MeetingLocation from 'owa-calendar-scheduling-service/lib/schema/MeetingLocation';
import type PersonaPostalAddress from 'owa-service/lib/contract/PersonaPostalAddress';
import type PostalAddressAttributedValue from 'owa-service/lib/contract/PostalAddressAttributedValue';
import type LocationPersonaType from 'owa-calendar-scheduling-service/lib/schema/LocationPersonaType';

/**
 * Convert FindMeetingLocations response object to Location PersonaTypes
 */
export default function meetingLocationResponseToLocationPersonaType(
    findMeetingLocationsResponse: FindMeetingLocationsResponse
): LocationPersonaType[] {
    let personas: LocationPersonaType[] = [];
    if (findMeetingLocationsResponse) {
        if (findMeetingLocationsResponse.MeetingLocations) {
            findMeetingLocationsResponse.MeetingLocations.map((meetingLocation, index) => {
                const locationAddress = meetingLocation.MeetingLocation.Address;
                const coordinates = meetingLocation.MeetingLocation.Coordinates;
                const address: PostalAddressAttributedValue = {
                    Attributions: ['0'],
                    Value: {
                        LocationUri: meetingLocation.MeetingLocation.LocationUri,
                        Street: locationAddress?.Street,
                        City: locationAddress?.City,
                        State: locationAddress?.State,
                        Country: locationAddress?.CountryOrRegion,
                        PostalCode: locationAddress?.PostalCode,
                        Latitude: coordinates?.Latitude,
                        Longitude: coordinates?.Longitude,
                        LocationSource: getOwaLocationSource(meetingLocation.MeetingLocation),
                        Type: 'Business',
                    } as PersonaPostalAddress,
                };

                let availability = meetingLocation.MeetingLocation.Availability;

                // Bug https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/44777
                // Tenant index search is not setting the location UniqueId. This is a client workaround to and delete this code when the bug is fixed
                const locationId = meetingLocation.MeetingLocation.UniqueId
                    ? meetingLocation.MeetingLocation.UniqueId
                    : meetingLocation.MeetingLocation.LocationUri;
                personas.push({
                    PersonaId: {
                        Id: locationId,
                    },
                    DisplayName: meetingLocation.MeetingLocation.DisplayName,
                    PersonaTypeString:
                        meetingLocation.MeetingLocation.EntityType == 'ConferenceRoom'
                            ? 'Room'
                            : 'Place',
                    EntityType: meetingLocation.MeetingLocation.EntityType,
                    EmailAddress: {
                        EmailAddress: meetingLocation.MeetingLocation.LocationEmailAddress,
                        Name: meetingLocation.MeetingLocation.DisplayName,
                    } as EmailAddressWrapper,
                    FeedbackId: locationId,
                    RelevanceHint: meetingLocation.RelevanceHint,
                    RelevanceScore: meetingLocation.RelevanceScore,
                    BuildingDisplayName: meetingLocation.MeetingLocation.BuildingDisplayName,
                    Availability: availability,
                    BusinessAddressesArray: [address],
                    Capacity: meetingLocation.MeetingLocation.Capacity,
                    NumberOfAvailableTimeSlots: meetingLocation.NumberOfFreeOccurrences,
                    TotalNumberOfTimeSlots: findMeetingLocationsResponse.TotalOccurrences,
                    IsPartialAvailability:
                        findMeetingLocationsResponse.IsRecurrenceExpansionPartial,
                    AudioDeviceName: meetingLocation.MeetingLocation.AudioDeviceName,
                    DisplayDeviceName: meetingLocation.MeetingLocation.DisplayDeviceName,
                    VideoDeviceName: meetingLocation.MeetingLocation.VideoDeviceName,
                    IsWheelChairAccessible: meetingLocation.MeetingLocation.IsWheelChairAccessible,
                } as LocationPersonaType);
            });
        }
    }
    return personas;
}

/*
 * Gets Location Source
 * 25154: LAS - Rationalize LocationType and LocationSource values to align with EVent LocationSource
 */
function getOwaLocationSource(location: MeetingLocation): LocationSourceType {
    if (location.EntityType == 'ConferenceRoom') {
        return 'Resource';
    } else if (location.LocationSource == 'Bing') {
        return 'LocationServices';
    } else {
        return 'Device';
    }
}
